import { defineStore, type StoreDefinition } from 'pinia'
import {reactive, ref} from 'vue'
import { fetchData } from '@/api/query';
import type {ICartItem} from '@utills/types'


export const useCartsStore: StoreDefinition<string> = defineStore('carts', () => {
  const carts = ref<ICartItem[]>([])
  const tax = ref(0)
  const shipping = ref(0)
  const spinnings = ref<number[]>([])
  const discount = ref(0)
  const total = ref(0)
  const postcode = ref(null)
  const currency = ref({
    code: 'USD',
    name: 'US Dollar',
    symbol: '$'
  })
  const cartResponse = reactive({
    couponCode: '',
    isFullyDiscounted: false
  });
  const paymentMethod = import.meta.env.VITE_PAYMENTS_TYPE


  const fetchCarts = async () => {
    try {
      const response:any = await fetchData('cart', 'get');
      response?.data.products.forEach((product: ICartItem) => {
        const cart = carts.value.find((cart) => cart.id === product.id)
        if (cart) {
          cart.count = product.count
        } else {
          carts.value.push(product)
        }
      })

      //todo, maybe we need to do it, maybe not
      // when user refreshs the page we start with clean slate
      // when user clicks on remove, we remove it manually
      //remove stuff not in products

      tax.value = response.data.tax

      total.value = response.data.total

      // no cycle
      // cycle.value = response.data.cycle?.price ?? null

      shipping.value = response.data.shipping

      discount.value = response.data.discountTotal

      cartResponse.couponCode = response.data.coupon_code ?? ''

      cartResponse.isFullyDiscounted = response.data.isFullyDiscounted ?? false

      postcode.value = response.data.postcode ?? null

      // Update currency information from API response
      if (response.data.currency) {
        currency.value = {
          code: response.data.currency.code || 'USD',
          name: response.data.currency.name || 'US Dollar',
          symbol: response.data.currency.symbol || '$'
        }
      }

      clearSpinnings()
    } catch (error) {
      console.error('Error fetching accessories:', error);
    }
  };

  fetchCarts()

  const setDiscount = async (discount: any) => {
    const response:any = await fetchData('cart/setDiscount', 'post', {
      discount,
    });
    fetchCarts()
    return response
  }

  const unsetDiscount = async () => {
    await fetchData('cart/unsetDiscount', 'post');
    fetchCarts()
  }

	  const setCustomerEmail = async (
	    payload: { email?: string | null; phone?: string | null } | string
	  ) => {
	    try {
	      const requestBody: { email?: string | null; phone?: string | null } = {}
	
	      if (typeof payload === 'string') {
	        if (payload) {
	          requestBody.email = payload
	        }
	      } else {
	        if (payload.email) {
	          requestBody.email = payload.email
	        }
	        if (payload.phone) {
	          requestBody.phone = payload.phone
	        }
	      }
	
	      // Don't call the API if we have neither email nor phone
	      if (!requestBody.email && !requestBody.phone) {
	        return
	      }
	
	      const response: any = await fetchData('cart/setCustomer', 'post', requestBody);
	
	      // Update cart data immediately with response
	      if (response?.data) {
	        const data = response.data;
	
	        // Update all cart data from setCustomer response
	        total.value = data.total;
	        tax.value = data.tax;
	        shipping.value = data.shipping;
	        discount.value = data.discountTotal;
	        cartResponse.couponCode = data.coupon_code ?? '';
	        cartResponse.isFullyDiscounted = data.isFullyDiscounted ?? false;
	        postcode.value = data.postcode ?? null;
	
	        // Update currency information
	        if (data.currency) {
	          currency.value = {
	            code: data.currency.code || 'USD',
	            name: data.currency.name || 'US Dollar',
	            symbol: data.currency.symbol || '$'
	          }
	        }
	
	        // Update products
	        if (data.products) {
	          carts.value.length = 0; // Clear existing
	          data.products.forEach((product: ICartItem) => {
	            carts.value.push(product);
	          });
	        }
	      }
	
	      return response;
	    } catch (error: any) {
	      console.error('Error setting customer contact info:', error);
	      throw error;
	    }
	  }

  const setShipment = async (postcode: any) => {
    try {
      const response: any = await fetchData('cart/setShipping', 'post', {
        postcode,
      });
      fetchCarts()
      return response
    } catch (error: any) {
      // Handle both frontend zone check and backend validation errors
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      } else if (error.message) {
        throw error;
      } else {
        throw new Error('Invalid postal code');
      }
    }
  }

  const updateCount = async (cartId: number, increment = true) => {
    addtoSpinning(cartId)
    await fetchData('cart/incrementItem', 'post', {
      product_id: cartId,
      increment
    });
    const cart = carts.value.find((cart) => cart.id === cartId)
    if (cart) {
      if (increment) {
        cart.count++
      } else {
        if (cart.count > 1) {
          cart.count--
        } else {
          removeCart(cartId)
          cart.count = 0
        }
      }
    }
    fetchCarts()
  }

  const addCart = (cart: ICartItem, increment = true) => {
    // addtoSpinning(cart.id)
    const existingCart = carts.value.find((c) => c.id === cart.id)
    if (increment) {
      if (existingCart) {
        updateCount(cart.id)
      } else {
        carts.value.push(cart)
        updateCount(cart.id)
      }
    } else {
      updateCount(cart.id, false)
    }
    // fetchCarts()
  }

  const removeCart = async (cartId: number) => {
    addtoSpinning(cartId)
    await fetchData('cart/remove', 'post', {
      product_id: cartId,
    });
    const index = carts.value.findIndex((cart) => cart.id === cartId)
    if (index !== -1) {
      carts.value.splice(index, 1)
    }
    fetchCarts()
  }

  const getCount = (cart: ICartItem) => {
    const existingCart = carts.value.find((c) => c.id === cart.id)
    if (existingCart) {
      return existingCart.count;
    } else {
      return 0;
    }
  }

  const addtoSpinning = (cartId: number) => {
    spinnings.value.push(cartId)
  }

  const getSpinning = (cartId: number) => {
    return spinnings.value.find((c) => c == cartId)
  }

  const clearSpinnings = () => {
    spinnings.value.splice(0, spinnings.value.length)
  }

  const clearCart = async () => {
    await fetchData('cart/clear', 'post', { });
    carts.value.splice(0, carts.value.length)
    fetchCarts()
  }

  const calculateTax = () => {
    return Number(tax.value)
  }

  const calculateTotal = () => {
    return Number(total.value)
  }

  const calculateDiscount = () => {
    return Number(discount.value)
  }


  const calculateCycle = () => {
    return Number(0);
  }


  const calculateShipping = () => {
    return Number(shipping.value)
  }

  const getCarts = () => {
    return carts.value
  }

  return {
    carts,
    tax,
    shipping,
    postcode,
    currency,
    setDiscount,
    unsetDiscount,
    setCustomerEmail,
    setShipment,
    updateCount,
    removeCart,
    calculateTotal,
    calculateDiscount,
    calculateShipping,
    calculateCycle,
    calculateTax,
    addCart,
    clearCart,
    getCount,
    getSpinning,
    getCarts,
    fetchCarts,
    spinnings,
    discount,
    total,
    cartResponse,
    paymentMethod
  }
})
